import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    DialogActions,
    CardHeader,
    CardContent,
    Box,
    Typography,
    LinearProgress,
    IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../index";
import { setModal } from "../../../../Common/App";
import { DialogCard } from "../../../../../Components";

export const ChangePassword = () => {
    const dispatch = useDispatch();
    const items = useSelector((store) => store.users);
    const [disabled, setDisabled] = useState(true);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [data, setData] = useState({
        id: "",
        old_password: "",
        password: "",
        password_confirmation: "",
    });
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [newPasswordFocused, setNewPasswordFocused] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");

    useEffect(() => {
        setData((prevData) => ({
            ...prevData,
            id: items.selected?.id ?? "",
        }));
    }, [items.selected]);

    const handleChange = (e) => {
        setDisabled(false);
        setValidationMessage("");
        const { id, value } = e.target;
        setData((prev) => ({
            ...prev,
            [id]: value,
        }));

        if (id === "password") {
            const strength = calculatePasswordStrength(value);
            setPasswordStrength(strength);
        }
    };

    const handleSubmit = async () => {
        if (!data.old_password?.trim()) {
            setValidationMessage("Current password is required.");
            return;
        }
        if (!data.password?.trim() || !data.password_confirmation?.trim()) {
            setValidationMessage("New password and confirmation are required to proceed.");
            return;
        }
        if (data.password !== data.password_confirmation) {
            setValidationMessage("New password and confirmation do not match.");
            return;
        }
        setValidationMessage("");
        setDisabled(true);
        const payload = {
            id: data.id,
            old_password: data.old_password,
            password: data.password,
            password_confirmation: data.password_confirmation,
        };
        await changePassword({ dispatch, data: payload }).then((res) => {
            if (res["success"]) {
                dispatch(setModal({ component: null, open: false }));
            }
            setDisabled(false);
        });
    };

    const togglePasswordVisibility = (type) => {
        setShowPassword((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[@$!%*?&#]/.test(password)) strength++;
        return strength;
    };

    const getHelperText = (field) => {
        if (field === "old_password") {
            return (
                items.error?.old_password?.[0] ??
                items.error?.old_password ??
                ""
            );
        }
        if (field === "password") {
            if (validationMessage) return validationMessage;
            return items.error?.password?.[0] ?? items.error?.password ?? "";
        }
        if (field === "password_confirmation") {
            if (validationMessage) return validationMessage;
            return (
                items.error?.password_confirmation?.[0] ??
                items.error?.password_confirmation ??
                ""
            );
        }
        return "";
    };

    return (
        <DialogCard>
            <CardHeader
                title="Change Password"
                subheader="Please update your password by filling the required fields below."
            />
            <CardContent>
                <Box my={2}>
                    <TextField
                        fullWidth
                        label="Current Password"
                        type={showPassword.current ? "text" : "password"}
                        id="old_password"
                        name="old_password"
                        value={data.old_password}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility("current")}
                                    edge="end"
                                >
                                    {showPassword.current ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            ),
                        }}
                        helperText={getHelperText("old_password")}
                    />
                </Box>

                <Box my={2}>
                    <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword.new ? "text" : "password"}
                        id="password"
                        name="password"
                        value={data.password}
                        onChange={handleChange}
                        onFocus={() => setNewPasswordFocused(true)}
                        onBlur={() => setNewPasswordFocused(false)}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility("new")}
                                    edge="end"
                                >
                                    {showPassword.new ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            ),
                        }}
                        helperText={getHelperText("password")}
                    />
                    {(newPasswordFocused || data.password.length > 0) && (
                        <Box
                            mt={1}
                            role="status"
                            aria-live="polite"
                            aria-label={`Password strength: ${passwordStrength < 2 ? "weak" : passwordStrength < 3 ? "medium" : "strong"}`}
                        >
                            <Typography variant="caption" color="textSecondary">
                                Password Strength
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={(passwordStrength / 4) * 100}
                                sx={{ height: 6, borderRadius: 1 }}
                                color={
                                    passwordStrength < 2
                                        ? "error"
                                        : passwordStrength < 3
                                            ? "warning"
                                            : "success"
                                }
                            />
                            <Typography variant="caption" color="textSecondary">
                                Use 8–16 characters with letters, numbers, and symbols (matches server rules).
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box my={2}>
                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showPassword.confirm ? "text" : "password"}
                        id="password_confirmation"
                        name="password_confirmation"
                        value={data.password_confirmation}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility("confirm")}
                                    edge="end"
                                >
                                    {showPassword.confirm ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            ),
                        }}
                        helperText={getHelperText("password_confirmation")}
                    />
                </Box>
            </CardContent>

            <DialogActions sx={{ mb: 2, mr: 2 }}>
                <Button
                    onClick={() => dispatch(setModal({ component: null, open: false }))}
                    variant="outlined"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    disabled={disabled}
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                >
                    Change Password
                </Button>
            </DialogActions>
        </DialogCard>
    );
};
